import { API_ENDPOINTS, createApiUrl, getAuthToken } from '../config/api';

export interface TeamMember {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  professionalRole?: string;
  active: boolean;
  createdAt: string;
}

export interface AccountOwner {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface AccountInfo {
  id: string;
  name: string;
  farmId: string;
  farmName: string;
  maxSubUsers: number;
  currentSubUsers: number;
}

export interface CorporateUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  corporateRole: string;
  active: boolean;
  canManage: boolean;
  assignedAt: string;
}

export interface TeamData {
  account: AccountInfo & {
    corporateAccountId?: string | null;
    corporateName?: string | null;
  };
  owner: AccountOwner;
  subUsers: TeamMember[];
  corporateUsers: CorporateUser[];
}

export interface InviteTeamMemberData {
  email: string;
  firstName: string;
  lastName: string;
  professionalRole?: string; // "employee" | "consultant"
}

export interface TeamStats {
  current: number;
  max: number;
  available: number;
  active: number;
  inactive: number;
  usagePercentage: number;
}

class TeamService {
  /**
   * Get all team members for the account owner
   */
  async getTeamMembers(): Promise<TeamData> {
    const url = createApiUrl(API_ENDPOINTS.TEAM_SUB_USERS);
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to fetch team members');
    }

    return response.json();
  }

  /**
   * Invite/create a new team member (sub-user)
   */
  async inviteTeamMember(data: InviteTeamMemberData): Promise<any> {
    const url = createApiUrl(API_ENDPOINTS.TEAM_INVITE_SUB_USER);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAuthToken()}`
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to invite team member');
    }

    return response.json();
  }

  // Update team member role - DISABLED: All sub-users are treated equally
  // async updateMemberRole(userId: string, role: string): Promise<any> {
  //   const url = createApiUrl(`/api/users/sub-users/${userId}/role`);
    
  //   const response = await fetch(url, {
  //     method: 'PATCH',
  //     headers: {
  //       'Content-Type': 'application/json',
  //       'Authorization': `Bearer ${getAuthToken()}`
  //     },
  //     body: JSON.stringify({ role })
  //   });

  //   if (!response.ok) {
  //     const error = await response.json().catch(() => ({}));
  //     throw new Error(error.error || 'Failed to update team member role');
  //   }

  //   return response.json();
  // }

  /**
   * Remove team member (sub-user)
   */
  async removeMember(userId: string): Promise<any> {
    const url = createApiUrl(`/api/users/sub-users/${userId}`);
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to remove team member');
    }

    return response.json();
  }

  /**
   * Get usage statistics for team members
   */
  async getUsageStats(): Promise<TeamStats> {
    const url = createApiUrl(API_ENDPOINTS.TEAM_SUB_USER_STATS);
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to fetch usage statistics');
    }

    return response.json();
  }

  /**
   * Get all farms the current user has access to
   */
  async getMyFarms(): Promise<UserFarm[]> {
    const url = createApiUrl('/api/users/my-farms');
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to fetch farm access list');
    }

    const data = await response.json();
    return data.farms || [];
  }

  /**
   * Get pending invitations for the farm
   */
  async getPendingInvitations(): Promise<PendingInvitation[]> {
    const url = createApiUrl('/api/users/pending-invitations');
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to fetch pending invitations');
    }

    const data = await response.json();
    return data.invitations || [];
  }

  /**
   * Cancel a pending invitation
   */
  async cancelInvitation(invitationId: string): Promise<any> {
    const url = createApiUrl(`/api/users/pending-invitations/${invitationId}`);
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to cancel invitation');
    }

    return response.json();
  }

  /**
   * Resend a pending invitation
   */
  async resendInvitation(invitationId: string): Promise<any> {
    const url = createApiUrl(`/api/users/pending-invitations/${invitationId}/resend`);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`
      }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to resend invitation');
    }

    return response.json();
  }
}

export interface UserFarm {
  farmId: string;
  farmName: string;
  accountId: string;
  accountName: string;
  accessType: string;
  grantedBy: {
    firstName: string;
    lastName: string;
    email: string;
  };
  grantedAt: string;
}

export interface PendingInvitation {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  professionalRole: string;
  status: string;
  expiresAt: string;
  createdAt: string;
}

export default new TeamService();

